/* eslint-disable import/no-anonymous-default-export */
import Note from "../interfaces/mortgageNote";
import { CreateNoteParams } from "../pages/private-admin/NoteEditNew";
import axios from "../utils/axios";
import AdminApiTokenSupport from "./admin-token.support";

class AdminApi extends AdminApiTokenSupport {
  async login(email: string, password: string): Promise<string> {
    const resp = await axios.post<string>("/api/mortgageNotes/admin/login", {
      email,
      password,
    });
    return resp.data;
  }

  async register(data: any): Promise<string> {
    const resp = await axios.post<string>("/api/mortgageNotes/admin/register", {
      ...data,
    });
    return resp.data;
  }

  async recoverPassword(email: string): Promise<boolean> {
    const resp = await axios.post<boolean>(
      `/api/mortgageNotes/admin/password-recovery`,
      {
        email,
      }
    );
    return resp.data;
  }

  /**
   * Reset password
   * @param email
   * @returns
   */
  async resetPassword(newPassword: string, token: string): Promise<boolean> {
    const resp = await axios.post<boolean>(
      `/api/mortgageNotes/admin/password-reset`,
      {
        newPassword,
        token,
      }
    );
    return resp.data;
  }

  async setPassword(password: string, token: string): Promise<string> {
    const resp = await axios.post<string>(
      `/api/mortgageNotes/admin/set-password`,
      {
        password,
        token,
      }
    );
    return resp.data;
  }

  async createNote(data: CreateNoteParams): Promise<Note> {
    const formDataToSend = new FormData();

    // Create metadata objects for images and documents
    const imageMetadata: Record<
      string,
      {
        alt: string;
        order_position: number;
        tag_text?: string;
        tag_color?: string;
      }
    > = {};

    // Handle regular fields first
    Object.entries(data).forEach(([key, value]) => {
      // Skip files and images as we'll handle them separately
      if (
        key !== "propertyImages" &&
        key !== "loanPackagePdf" &&
        key !== "propertyDetailsExcel"
      ) {
        if (Array.isArray(value)) {
          // Handle arrays like highlights
          value.forEach((item) => {
            formDataToSend.append(`${key}[]`, String(item));
          });
        } else {
          formDataToSend.append(key, String(value));
        }
      }
    });

    // Handle property images with metadata
    if (Array.isArray(data.propertyImages)) {
      data.propertyImages.forEach((image) => {
        if (image && "file" in image && image.file instanceof File) {
          formDataToSend.append("propertyImages", image.file);
          imageMetadata[image.file.name] = {
            alt: image.alt || "",
            order_position: image.order_position,
            tag_text: image.tag_text,
            tag_color: image.tag_color,
          };
        }
      });
    }

    // Handle loan package PDF
    if (data.loanPackagePdf?.file instanceof File) {
      formDataToSend.append("loanPackagePdf", data.loanPackagePdf.file);
    }

    // Handle property details Excel if present
    if (data.propertyDetailsExcel?.file instanceof File) {
      formDataToSend.append(
        "propertyDetailsExcel",
        data.propertyDetailsExcel.file
      );
    }

    // Add image metadata to form data
    formDataToSend.append("image_metadata", JSON.stringify(imageMetadata));

    console.log(formDataToSend);

    const resp = await axios.post<Note>(
      `/api/mortgageNotes/admin/create-note`,
      formDataToSend,
      this.withAuthorization({
        "Content-Type": "multipart/form-data",
      })
    );
    return resp.data;
  }

  async updateNote(id: number, data: Partial<Note> | FormData): Promise<Note> {
    const resp = await axios.put<Note>(
      `/api/mortgageNotes/admin/update-note/${id}`,
      data,
      this.withAuthorization({
        "Content-Type":
          data instanceof FormData ? "multipart/form-data" : "application/json",
      })
    );
    return resp.data;
  }

  async sendNote(id: number): Promise<any> {
    const resp = await axios.post(
      `/api/mortgageNotes/admin/send-note/${id}`,
      {},
      this.withAuthorization()
    );
    return resp.data;
  }

  async deleteNote(id: number): Promise<any> {
    const resp = await axios.delete(
      `/api/mortgageNotes/admin/delete-note/${id}`,
      this.withAuthorization()
    );

    return resp.data;
  }
}

export default new AdminApi();
